import { useId, useState, type ReactNode } from "react";
import { Icon } from "@/components/ui/icon";
import { EmptyState, SkeletonChart } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { cn } from "@/lib/cn";

export interface TableColumn {
  key: string;
  header: string;
  numeric?: boolean;
}

export interface ChartFrameProps {
  title: string;
  note?: string;
  /** Legend entries. Present whenever there are 2+ series; omitted for one. */
  legend?: { label: string; color: string }[];
  /** The WCAG-clean twin of the chart. Every chart in this app has one. */
  table: { columns: TableColumn[]; rows: Record<string, ReactNode>[] };
  loading?: boolean;
  error?: string | null;
  /** True when there is genuinely nothing to plot yet. */
  empty?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  /** Height of the plot area only; the axis band is added on top of it. */
  height?: number;
  className?: string;
  children: ReactNode;
}

/**
 * Every chart is wrapped in this. It owns the four things a chart must have and
 * that are easy to forget: a title, a legend for 2+ series, loading/empty/error
 * states, and a table view.
 *
 * The table view is not optional decoration. Three light-mode series colours sit
 * below 3:1 against this app's surface (a documented, non-dismissable validator
 * WARN), and the relief channel for that is exactly this: every value is
 * readable as text, so nothing is gated behind hue or a hover tooltip.
 */
export function ChartFrame({
  title,
  note,
  legend,
  table,
  loading,
  error,
  empty,
  emptyTitle = "Nothing to plot yet",
  emptyBody,
  height = 240,
  className,
  children,
}: ChartFrameProps) {
  const [view, setView] = useState<"chart" | "table">("chart");
  const panelId = useId();

  const showLegend = (legend?.length ?? 0) >= 2;

  return (
    <section className={cn("flex flex-col rounded-xl border border-line bg-surface", className)}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold tracking-tight text-ink">{title}</h3>
          {note && <p className="mt-0.5 text-xs leading-normal text-muted">{note}</p>}
        </div>
        {!loading && !error && !empty && (
          <div
            role="group"
            aria-label={`${title} view`}
            className="flex shrink-0 items-center gap-0.5 rounded-lg border border-line bg-surface-sunken p-0.5"
          >
            <ViewToggle
              active={view === "chart"}
              onClick={() => setView("chart")}
              icon="chart-bar"
              label="Chart"
              controls={panelId}
            />
            <ViewToggle
              active={view === "table"}
              onClick={() => setView("table")}
              icon="table"
              label="Table"
              controls={panelId}
            />
          </div>
        )}
      </header>

      {showLegend && !loading && !error && !empty && view === "chart" && (
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-line px-4 py-2">
          {legend?.map((item) => (
            <li key={item.label} className="flex items-center gap-1.5">
              {/* Identity rides a coloured mark beside the text; the text itself
                  always wears an ink token so it stays legible. */}
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-ink-soft">{item.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div id={panelId} className="min-w-0 flex-1">
        {loading ? (
          <SkeletonChart height={height} />
        ) : error ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <Icon name="warning" size={24} className="mb-2 text-bad" />
            <p className="text-sm font-medium text-ink">Could not load this chart</p>
            <p className="mt-1 max-w-[40ch] text-sm leading-relaxed text-muted">{error}</p>
          </div>
        ) : empty ? (
          <EmptyState icon="chart-bar" title={emptyTitle} {...(emptyBody ? { body: emptyBody } : {})} />
        ) : view === "chart" ? (
          <div className="p-4 pt-3">{children}</div>
        ) : (
          <ChartTable columns={table.columns} rows={table.rows} />
        )}
      </div>
    </section>
  );
}

function ViewToggle({
  active,
  onClick,
  icon,
  label,
  controls,
}: {
  active: boolean;
  onClick: () => void;
  icon: "chart-bar" | "table";
  label: string;
  controls: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-controls={controls}
      title={`${label} view`}
      className={cn(
        "flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium transition-colors active:scale-[0.98]",
        active ? "bg-surface text-ink shadow-card" : "text-muted hover:text-ink",
      )}
    >
      <Icon name={icon} size={16} />
      <span className="sr-only sm:not-sr-only">{label}</span>
    </button>
  );
}

function ChartTable({ columns, rows }: { columns: TableColumn[]; rows: Record<string, ReactNode>[] }) {
  if (rows.length === 0) {
    return <EmptyState icon="table" title="No rows" />;
  }
  return (
    <div className="max-h-80 overflow-y-auto">
      <TableWrap>
        <THead>
          {columns.map((column) => (
            <TH key={column.key} {...(column.numeric ? { numeric: true } : {})}>
              {column.header}
            </TH>
          ))}
        </THead>
        <TBody>
          {rows.map((row, index) => (
            <TR key={index}>
              {columns.map((column, colIndex) =>
                colIndex === 0 ? (
                  <TDPrimary key={column.key}>{row[column.key]}</TDPrimary>
                ) : (
                  <TD key={column.key} {...(column.numeric ? { numeric: true } : {})}>
                    {row[column.key]}
                  </TD>
                ),
              )}
            </TR>
          ))}
        </TBody>
      </TableWrap>
    </div>
  );
}

/**
 * Shared tooltip. Recharts' default is a white box with a border and coloured
 * text; this restyles it to the app's surface with ink-token text and a
 * coloured dot carrying identity.
 */
export function ChartTooltip({
  active,
  payload,
  label,
  valueSuffix = "",
}: {
  active?: boolean;
  payload?: { name?: string; value?: number | string; color?: string; dataKey?: string }[];
  label?: string | number;
  valueSuffix?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="pointer-events-none rounded-lg border border-line bg-surface px-3 py-2 shadow-pop">
      {label !== undefined && label !== "" && (
        <p className="mb-1.5 text-xs font-medium text-ink">{label}</p>
      )}
      <ul className="flex flex-col gap-1">
        {payload.map((entry, index) => (
          <li key={index} className="flex items-center gap-2 text-xs">
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted">{entry.name}</span>
            <span className="ml-auto pl-3 font-mono tabular-nums text-ink">
              {entry.value}
              {valueSuffix}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
